import React, { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { formatDateTime } from '@/lib/dateFormat'
import { getSalesOrderHistoryApi, type SalesOrderHistory } from '@/services/salesOrders'
import type { GridLineItem } from './salesOrderGridUtils'

export interface SalesOrderLineHistoryModalProps {
  isOpen: boolean
  line: GridLineItem | null
  orderId?: string
  onClose: () => void
}

export function SalesOrderLineHistoryModal({
  isOpen,
  line,
  orderId,
  onClose,
}: SalesOrderLineHistoryModalProps): React.ReactElement | null {
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<SalesOrderHistory[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen || !orderId || !line) return

    let cancelled = false
    setLoading(true)
    setError(null)

    getSalesOrderHistoryApi(orderId)
      .then((items) => {
        if (cancelled) return
        // Filter by line_id if present
        const lineLogs = items.filter(
          (item) => !item.line_id || item.line_id === line.id,
        )
        setHistory(lineLogs.length > 0 ? lineLogs : items)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Không thể tải lịch sử thay đổi')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, orderId, line])

  if (!isOpen || !line) return null

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-slate-900/60 p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-3.5">
          <h3 className="font-bold text-slate-800 text-base">
            Lịch sử dòng hàng: {line.variant_code || 'Chưa chọn'} (SO: {orderId})
          </h3>
          <button
            type="button"
            className="text-slate-400 hover:text-slate-600 text-lg leading-none"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-5 text-sm">
          {loading && <p className="text-slate-500 py-4 text-center">Đang tải lịch sử...</p>}
          {error && <p className="text-rose-600 py-4 text-center">{error}</p>}
          {!loading && !error && history.length === 0 && (
            <p className="text-slate-400 py-8 text-center">Chưa có bản ghi lịch sử thay đổi nào.</p>
          )}
          {!loading && !error && history.length > 0 && (
            <div className="space-y-3">
              {history.map((log) => (
                <div
                  key={log.id}
                  className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs"
                >
                  <div className="flex items-center justify-between font-semibold text-slate-700">
                    <span className="font-mono text-emerald-700">{log.action}</span>
                    <span className="text-slate-500">{formatDateTime(log.at)}</span>
                  </div>
                  <div className="mt-1 text-slate-600">
                    Thực hiện bởi: <strong>{log.by || 'Hệ thống'}</strong>
                  </div>
                  {log.reason && (
                    <div className="mt-1 text-slate-700 font-medium">
                      Lý do: <span className="italic">{log.reason}</span>
                    </div>
                  )}
                  {log.after && (
                    <div className="mt-2 font-mono text-xs text-slate-600 bg-white p-2 rounded border border-slate-200 overflow-x-auto">
                      {JSON.stringify(log.after, null, 2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
          <Button variant="secondary" size="sm" type="button" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
